import { useState } from 'react';
import { RecoilRoot } from 'recoil';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import { Box } from '@mui/material';
import { Leva } from 'leva';
import { DesignSpacePanel } from 'features/optimization/components/DesignSpacePanel';
import { DisplayModelPanel } from 'features/file-browser/components/display/DisplayModelPanel';
import { DisplayCocoonPanel } from 'features/file-browser/components/display/DisplayCocoonPanel';
import { DisplayOthersPanel } from 'features/file-browser/components/display/DisplayOthersPanel';
import { FileBrowserPanel } from 'features/file-browser/components/FileBrowserPanel';
import { PerspectivePanel } from 'features/perspective/components/PerspectivePanel';
import { ThreeViewPanel } from 'features/three-view/components/ThreeViewPanel';
import { MorphingPanel } from 'features/morphing/components/MorphingPanel';
import { OptimizationPanel } from 'features/optimization/components/OptimizationPanel';
import { APIPanel } from 'features/api-panel/components/APIPanel';
import { ChartPanel } from 'features/chart/components/ChartPanel';
import { InitialFetch } from 'app/bootstrap/InitialFetch';
import { AuthGate } from 'app/providers/AuthGate';
import { panelSx } from 'app/layout/panelStyles';
import { PriceSummaryBar } from 'features/price-summary/components/PriceSummaryBar';

const SUMMARY_BAR_HEIGHT_PX = 46;

export const MainContainer: React.FC = () => {
  const [threeViewSize, setThreeViewSize] = useState({ width: 0, height: 0 });

  const handleThreeViewHeightResize = (size: number) => {
    setThreeViewSize((prev) => ({
      ...prev,
      height: ((window.innerHeight - SUMMARY_BAR_HEIGHT_PX) * size) / 100,
    }));
  };

  const handleThreeViewWidthResize = (size: number) => {
    setThreeViewSize((prev) => ({ ...prev, width: (window.innerWidth * size) / 100 }));
  };

  return (
    <RecoilRoot>
      <InitialFetch />
      <AuthGate>
        {/* Leva は viewport 全体に対する position: fixed で配置される。
            ThreeViewPanel 内に置くと panel の relative parent に拘束されるため、
            最上位 (MainContainer 直下) に出して画面全体で floating させる。 */}
        <Leva collapsed={true} />
        <Box
          component="div"
          sx={{
            border: '3px solid gray',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflow: 'hidden',
            width: '100vw',
          }}
        >
          <Box
            component="div"
            sx={{
              borderBottom: '3px solid gray',
              boxSizing: 'border-box',
              flexShrink: 0,
              height: `${SUMMARY_BAR_HEIGHT_PX}px`,
              width: '100%',
              backgroundColor: '#1e1e1e',
            }}
          >
            <PriceSummaryBar />
          </Box>

          <Box component="div" sx={{ width: '100%', flex: 1, minHeight: 0 }}>
            <PanelGroup direction="horizontal">

            {/* 左28% */}
            <Panel defaultSize={28}>
              <PanelGroup direction="vertical">

                {/* 15% */}
                <Panel defaultSize={15}>
                  <Box
                    component="div"
                    sx={{
                      ...panelSx,
                      borderRight: '3px solid gray',
                      borderBottom: '3px solid gray'
                    }}
                  >
                    <DesignSpacePanel subtitle='Design Specifications' />
                  </Box>
                </Panel>

                <PanelResizeHandle />

                {/* 20% */}
                <Panel defaultSize={20}>
                  <Box
                    component="div"
                    sx={{
                      ...panelSx,
                      borderRight: '3px solid gray',
                      borderBottom: '3px solid gray'
                    }}
                  >
                    <DisplayModelPanel subtitle='Display Base Model' />
                  </Box>
                </Panel>

                <PanelResizeHandle />

                {/* 15% */}
                <Panel defaultSize={15}>
                  <Box
                    component="div"
                    sx={{
                      ...panelSx,
                      borderRight: '3px solid gray',
                      borderBottom: '3px solid gray'
                    }}
                  >
                    <DisplayCocoonPanel subtitle='Display Cocoon Models' />
                  </Box>
                </Panel>

                <PanelResizeHandle />

                {/* 10% */}
                <Panel defaultSize={10}>
                  <Box
                    component="div"
                    sx={{
                      ...panelSx,
                      borderRight: '3px solid gray',
                      borderBottom: '3px solid gray'
                    }}
                  >
                    <DisplayOthersPanel subtitle='Display Other Models' />
                  </Box>
                </Panel>

                <PanelResizeHandle />

                {/* 40% */}
                <Panel defaultSize={40}>
                  <Box
                    component="div"
                    sx={{
                      ...panelSx,
                      borderRight: '3px solid gray'
                    }}
                  >
                    <FileBrowserPanel subtitle='File Server Browsing' />
                  </Box>
                </Panel>

              </PanelGroup>
            </Panel>

            <PanelResizeHandle />

            {/* 中央47% */}
            <Panel defaultSize={47} onResize={handleThreeViewWidthResize}>
              <PanelGroup direction="vertical">

                {/* 上側5% */}
                <Panel defaultSize={5}>
                  <Box
                    component="div"
                    sx={{
                      ...panelSx,
                      borderBottom: '3px solid gray',
                      padding: 0,
                      gap: 0
                    }}
                  >
                    <PerspectivePanel />
                  </Box>
                </Panel>

                <PanelResizeHandle />

                {/* 真ん中50% */}
                <Panel defaultSize={50} onResize={handleThreeViewHeightResize}>
                  <Box
                    component="div"
                    sx={{
                      ...panelSx,
                      borderBottom: '3px solid gray'
                    }}
                  >
                    <ThreeViewPanel
                      width={threeViewSize.width}
                      height={threeViewSize.height}
                    />
                  </Box>
                </Panel>

                <PanelResizeHandle />

                {/* 下側45% */}
                <Panel defaultSize={45}>
                  <PanelGroup direction="horizontal">

                    {/* 左側50% */}
                    <Panel defaultSize={50}>
                      <Box
                        component="div"
                        sx={{
                          ...panelSx,
                          boxSizing: 'border-box'
                        }}
                      >
                        <ChartPanel />
                      </Box>
                    </Panel>

                    <PanelResizeHandle />

                    {/* 右側50% */}
                    <Panel defaultSize={50}>
                      <Box
                        component="div"
                        sx={{
                          ...panelSx,
                          borderLeft: '3px solid gray',
                          boxSizing: 'border-box',
                          padding: 0
                        }}
                      >
                        <APIPanel />
                      </Box>
                    </Panel>

                  </PanelGroup>
                </Panel>

              </PanelGroup>
            </Panel>

            <PanelResizeHandle />

            {/* 右25% */}
            <Panel defaultSize={25}>
              <PanelGroup direction="vertical">

                {/* 上側50% */}
                <Panel defaultSize={50}>
                  <Box
                    component="div"
                    sx={{
                      ...panelSx,
                      paddingTop: 0,
                      borderBottom: '3px solid gray',
                      borderLeft: '3px solid gray'
                    }}
                  >
                    <MorphingPanel />
                  </Box>
                </Panel>

                <PanelResizeHandle />

                {/* 下側50% */}
                <Panel defaultSize={50}>
                  <Box
                    component="div"
                    sx={{
                      ...panelSx,
                      paddingTop: 0,
                      borderLeft: '3px solid gray'
                    }}
                  >
                    <OptimizationPanel />
                  </Box>
                </Panel>

              </PanelGroup>
            </Panel>

            </PanelGroup>
          </Box>
        </Box >
      </AuthGate>
    </RecoilRoot>
  );
};
